import axios from 'axios';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export { API };
export const http = axios.create({ baseURL: API, withCredentials: true });

// Attach Bearer token from localStorage — crucial for iOS PWA / mobile
// where SameSite=None cookies may be stripped on cross-site requests.
http.interceptors.request.use((cfg) => {
  try {
    const t = localStorage.getItem('bp_token');
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
  } catch {}
  return cfg;
});

export const clientsApi = {
  list: (q) => http.get('/clients', { params: q ? { q } : {} }).then(r => r.data),
  listLite: () => http.get('/clients', { params: { lite: true } }).then(r => r.data),
  osContext: (id) => http.get(`/clients/${id}/os-context`).then(r => r.data),
  search: (q) => http.get('/clients', { params: q ? { q } : {} }).then(r => r.data),
  checkDuplicate: (document) => http.get('/clients/check-duplicate', { params: { document } }).then(r => r.data),
  get: (id) => http.get(`/clients/${id}`).then(r => r.data),
  create: (data) => http.post('/clients', data).then(r => r.data),
  update: (id, data) => http.patch(`/clients/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/clients/${id}`).then(r => r.data),
};
export const unitsApi = {
  list: (clientId) => http.get('/units', { params: clientId ? { client_id: clientId } : {} }).then(r => r.data),
  create: (data) => http.post('/units', data).then(r => r.data),
  update: (id, data) => http.patch(`/units/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/units/${id}`).then(r => r.data),
};
export const searchApi = {
  global: (q) => http.get('/search', { params: { q } }).then(r => r.data),
};
export const clientSummaryApi = {
  get: (id) => http.get(`/clients/${id}/summary`).then(r => r.data),
};
export const dashboardApi = {
  pestIncidence: (params = {}) => http.get('/dashboard/pest-incidence', { params }).then(r => r.data),
  neighborhoodVolume: (params = {}) => http.get('/dashboard/neighborhood-volume', { params }).then(r => r.data),
  breakEven: (params = {}) => http.get('/dashboard/break-even', { params }).then(r => r.data),
  kpis: () => http.get('/dashboard/kpis').then(r => r.data),
  todayOverview: (period = 'diario') => http.get('/dashboard/today-overview', { params: { period } }).then(r => r.data),
};
// Public (unauthenticated) proposal flow
export const publicProposalsApi = {
  get: (token) => axios.get(`${API}/public/proposals/${token}`).then(r => r.data),
  accept: (token, data) => axios.post(`${API}/public/proposals/${token}/accept`, data).then(r => r.data),
  pdfUrl: (token) => `${API}/public/proposals/${token}/pdf`,
};
export const techsApi = {
  list: (q) => http.get('/technicians', { params: q ? { q } : {} }).then(r => r.data),
  create: (data) => http.post('/technicians', data).then(r => r.data),
  update: (id, data) => http.patch(`/technicians/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/technicians/${id}`).then(r => r.data),
  sendWelcome: (id) => http.post(`/technicians/${id}/welcome`).then(r => r.data),
};
export const chemicalsApi = {
  list: (params) => http.get('/chemicals', { params: typeof params === 'string' ? { q: params } : params }).then(r => r.data),
  filterOptions: () => http.get('/chemicals/filter-options').then(r => r.data),
  create: (data) => http.post('/chemicals', data).then(r => r.data),
  update: (id, data) => http.patch(`/chemicals/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/chemicals/${id}`).then(r => r.data),
  importTemplateUrl: () => `${API}/chemicals/import-template.xlsx`,
  importFile: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/chemicals/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  fispqUrl: (id) => `${API}/chemicals/${id}/fispq`,
  uploadFispq: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post(`/chemicals/${id}/fispq`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  removeFispq: (id) => http.delete(`/chemicals/${id}/fispq`).then(r => r.data),
  seedBrazilianCatalog: () => http.post('/chemicals/seed-brazilian-catalog').then(r => r.data),
  anvisa: {
    stats: () => http.get('/chemicals/anvisa/stats').then(r => r.data),
    refresh: () => http.post('/chemicals/anvisa/refresh').then(r => r.data),
    bulkMatch: (threshold = 0.85) => http.post(`/chemicals/anvisa/bulk-match?apply_threshold=${threshold}`).then(r => r.data),
    suggest: (chemId) => http.get(`/chemicals/${chemId}/anvisa/suggest`).then(r => r.data),
    apply: (chemId, payload) => http.post(`/chemicals/${chemId}/anvisa/apply`, payload).then(r => r.data),
  },
};

export const agendaBoaApi = {
  config: {
    get: () => http.get('/integrations/agendaboa/config').then(r => r.data),
    update: (p) => http.patch('/integrations/agendaboa/config', p).then(r => r.data),
  },
  status: () => http.get('/integrations/agendaboa/status').then(r => r.data),
  preview: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/integrations/agendaboa/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  sync: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/integrations/agendaboa/sync', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  fields: () => http.get('/integrations/agendaboa/fields').then(r => r.data),
  runs: {
    create: (files) => {
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      return http.post('/integrations/agendaboa/runs', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }).then(r => r.data);
    },
    list: (limit = 20) => http.get('/integrations/agendaboa/runs', { params: { limit } }).then(r => r.data),
    get: (id) => http.get(`/integrations/agendaboa/runs/${id}`).then(r => r.data),
    updateTable: (id, tableId, body) => http.put(`/integrations/agendaboa/runs/${id}/tables/${tableId}`, body).then(r => r.data),
    preview: (id, strategy) => http.post(`/integrations/agendaboa/runs/${id}/preview`, { strategy }, { timeout: 300000 }).then(r => r.data),
    rows: (id, params) => http.get(`/integrations/agendaboa/runs/${id}/rows`, { params }).then(r => r.data),
    patchRow: (id, rowId, body) => http.patch(`/integrations/agendaboa/runs/${id}/rows/${rowId}`, body).then(r => r.data),
    apply: (id) => http.post(`/integrations/agendaboa/runs/${id}/apply`).then(r => r.data),
    cancel: (id) => http.post(`/integrations/agendaboa/runs/${id}/cancel`).then(r => r.data),
    retry: (id) => http.post(`/integrations/agendaboa/runs/${id}/retry`, null, { timeout: 300000 }).then(r => r.data),
    rollback: (id) => http.post(`/integrations/agendaboa/runs/${id}/rollback`).then(r => r.data),
    downloadReport: async (id) => {
      const r = await http.get(`/integrations/agendaboa/runs/${id}/report.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `agendaboa_${id}.csv`; a.click();
      URL.revokeObjectURL(url);
    },
  },
  legacyMergeReport: () => http.get('/integrations/agendaboa/legacy-merge-report').then(r => r.data),
  clientRepair: {
    preview: () => http.get('/integrations/agendaboa/client-repair/preview').then(r => r.data),
    apply: () => http.post('/integrations/agendaboa/client-repair/apply').then(r => r.data),
    batches: () => http.get('/integrations/agendaboa/client-repair/batches').then(r => r.data),
    undo: (id) => http.post(`/integrations/agendaboa/client-repair/undo/${id}`).then(r => r.data),
  },
  history: (limit = 50) => http.get('/integrations/agendaboa/history', { params: { limit } }).then(r => r.data),
  logDetail: (id) => http.get(`/integrations/agendaboa/history/${id}`).then(r => r.data),
  conflicts: {
    list: () => http.get('/integrations/agendaboa/conflicts').then(r => r.data),
    resolve: (id, body) => http.post(`/integrations/agendaboa/conflicts/${id}/resolve`, body).then(r => r.data),
  },
  taxonomy: {
    get: () => http.get('/integrations/agendaboa/taxonomy').then(r => r.data),
    update: (payload) => http.post('/integrations/agendaboa/taxonomy', payload).then(r => r.data),
  },
};
export const ordersApi = {
  list: (params) => http.get('/service-orders', { params }).then(r => r.data),
  get: (id) => http.get(`/service-orders/${id}`).then(r => r.data),
  create: (data) => http.post('/service-orders', data).then(r => r.data),
  update: (id, data) => http.patch(`/service-orders/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/service-orders/${id}`).then(r => r.data),
  validate: (id) => http.get(`/service-orders/${id}/validate`).then(r => r.data),
  uploadPhoto: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post(`/service-orders/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  removePhoto: (id, photoId) => http.delete(`/service-orders/${id}/photos/${photoId}`).then(r => r.data),
};
export const uploadsApi = {
  companyLogo: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/uploads/company-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  companyLogoDark: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/uploads/company-logo-dark', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  removeCompanyLogoDark: () => http.delete('/uploads/company-logo-dark').then(r => r.data),
  rtSignature: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post('/uploads/rt-signature', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  removeRtSignature: () => http.delete('/uploads/rt-signature').then(r => r.data),
  osAttachment: (osId, file, kind = 'generic') => {
    const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
    return http.post(`/uploads/os-attachment/${osId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  remove: (fileId) => http.delete(`/files/${fileId}`).then(r => r.data),
};
export const companiesApi = {
  mine: () => http.get('/companies/me').then(r => r.data),
  update: (id, data) => http.patch(`/companies/${id}`, data).then(r => r.data),
};
export const operatorRoutineApi = {
  today: (params) => http.get('/operator-routine', { params }).then(r => r.data),
};
export const permissionsApi = {
  me: () => http.get('/auth/me/permissions').then(r => r.data),
  catalog: () => http.get('/auth/admin/modules').then(r => r.data),
  updateUser: (userId, modules) => http.patch(`/auth/admin/users/${userId}`, { module_permissions: modules }).then(r => r.data),
};
export const catalogApi = {
  list: () => http.get('/service-catalog').then(r => r.data),
  create: (data) => http.post('/service-catalog', data).then(r => r.data),
  update: (id, data) => http.patch(`/service-catalog/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/service-catalog/${id}`).then(r => r.data),
};
export const certsApi = {
  list: (q) => http.get('/certificates', { params: q ? { q } : {} }).then(r => r.data),
};
export const auditApi = {
  list: (params) => http.get('/audit-log', { params }).then(r => r.data),
};
export const devicesApi = {
  list: () => http.get('/my/devices').then(r => r.data),
  remove: (id) => http.delete(`/auth/biometric/${id}`).then(r => r.data),
};
export const metricsApi = {
  overview: () => http.get('/metrics/overview').then(r => r.data),
};

// ---------- Meta ----------
export const metaApi = {
  cargos: () => http.get('/meta/cargos').then(r => r.data),
  privileges: () => http.get('/meta/privileges').then(r => r.data),
  cargoDefaults: (cargo) => http.get('/meta/cargo-defaults', { params: { cargo } }).then(r => r.data),
};

// ---------- Team ----------
export const teamApi = {
  list: (q) => http.get('/team', { params: q ? { q } : {} }).then(r => r.data),
  create: (data) => http.post('/team', data).then(r => r.data),
  update: (id, data) => http.patch(`/team/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/team/${id}`).then(r => r.data),
};

// ---------- Proposals ----------
export const pestTemplatesApi = {
  list: () => http.get('/pest-templates').then(r => r.data),
  create: (data) => http.post('/pest-templates', data).then(r => r.data),
  update: (id, data) => http.put(`/pest-templates/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/pest-templates/${id}`).then(r => r.data),
};

export const proposalsApi = {
  list: (params) => http.get('/proposals', { params: params || {} }).then(r => r.data),
  get: (id) => http.get(`/proposals/${id}`).then(r => r.data),
  sendWhatsApp: (id, body = {}) => http.post(`/proposals/${id}/whatsapp`, body).then(r => r.data),
  waAccept: (id, confirm) => http.post(`/proposals/${id}/whatsapp-accept`, { confirm }).then(r => r.data),
  stats: () => http.get('/proposals/stats').then(r => r.data),
  create: (data) => http.post('/proposals', data).then(r => r.data),
  update: (id, data) => http.patch(`/proposals/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/proposals/${id}`).then(r => r.data),
  publicLink: (id) => http.post(`/proposals/${id}/public-link`).then(r => r.data),
  changeStatus: (id, data) => http.post(`/proposals/${id}/status`, data).then(r => r.data),
  registerContact: (id, data) => http.post(`/proposals/${id}/contacts`, data).then(r => r.data),
  markLost: (id, data) => http.post(`/proposals/${id}/mark-lost`, data).then(r => r.data),
  duplicate: (id) => http.post(`/proposals/${id}/duplicate`).then(r => r.data),
  archive: (id) => http.post(`/proposals/${id}/archive`).then(r => r.data),
  createOrder: (id) => http.post(`/proposals/${id}/create-order`).then(r => r.data),
};

// ---------- Funnel ----------
export const funnelApi = {
  list: () => http.get('/funnel').then(r => r.data),
  create: (data) => http.post('/funnel', data).then(r => r.data),
  update: (id, data) => http.patch(`/funnel/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/funnel/${id}`).then(r => r.data),
  detail: (id) => http.get(`/funnel/${id}/detail`).then(r => r.data),
  interact: (id, data) => http.post(`/funnel/${id}/interactions`, data).then(r => r.data),
  matchClient: (data) => http.post('/funnel/match-client', data).then(r => r.data),
  stages: () => http.get('/funnel/stages').then(r => r.data),
  saveStages: (data) => http.put('/funnel/stages', data).then(r => r.data),
  owners: () => http.get('/funnel/owners').then(r => r.data),
  dashboard: (params) => http.get('/funnel/dashboard', { params }).then(r => r.data),
  tasks: (params) => http.get('/funnel/tasks', { params }).then(r => r.data),
  addTask: (leadId, data) => http.post(`/funnel/${leadId}/tasks`, data).then(r => r.data),
  patchTask: (id, data) => http.patch(`/funnel/tasks/${id}`, data).then(r => r.data),
  automation: () => http.get('/funnel/automation').then(r => r.data),
  saveAutomation: (data) => http.put('/funnel/automation', data).then(r => r.data),
  ai: {
    summary: (leadId) => http.post(`/funnel/${leadId}/ai/summary`).then(r => r.data),
    approveSummary: (leadId, summary) => http.post(`/funnel/${leadId}/ai/summary/approve`, { summary }).then(r => r.data),
    reply: (leadId) => http.post(`/funnel/${leadId}/ai/reply`).then(r => r.data),
    prioritize: () => http.post('/funnel/ai/prioritize').then(r => r.data),
    applyPriority: (data) => http.post('/funnel/ai/apply-priority', data).then(r => r.data),
  },
};

// ---------- Contracts ----------
export const contractsApi = {
  list: () => http.get('/contracts').then(r => r.data),
  create: (data) => http.post('/contracts', data).then(r => r.data),
  update: (id, data) => http.patch(`/contracts/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/contracts/${id}`).then(r => r.data),
  mrr: () => http.get('/contracts/mrr').then(r => r.data),
};

// ---------- Agenda ----------
export const agendaApi = {
  list: () => http.get('/agenda').then(r => r.data),
  create: (data) => http.post('/agenda', data).then(r => r.data),
  update: (id, data) => http.patch(`/agenda/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/agenda/${id}`).then(r => r.data),
  gcalStatus: () => http.get('/agenda/gcal/status').then(r => r.data),
  gcalPull: () => http.post('/agenda/gcal/pull').then(r => r.data),
};

// ---------- POPs ----------
export const popsApi = {
  list: (q) => http.get('/pops', { params: q ? { q } : {} }).then(r => r.data),
  create: (data) => http.post('/pops', data).then(r => r.data),
  update: (id, data) => http.patch(`/pops/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/pops/${id}`).then(r => r.data),
};

// ---------- Laudos ----------
const openBlob = async (url, params, download) => {
  const r = await http.get(url, { params, responseType: 'blob' });
  const name = r.headers['x-filename'] || (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'documento';
  const href = URL.createObjectURL(r.data);
  if (download) { const a = document.createElement('a'); a.href = href; a.download = name; a.click(); }
  else window.open(href, '_blank');
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
};
export const technicalReportsApi = {
  list: (params) => http.get('/technical-reports', { params }).then(r => r.data),
  fromOs: (osId) => http.post(`/technical-reports/from-os/${osId}`).then(r => r.data),
  get: (id) => http.get(`/technical-reports/${id}`).then(r => r.data),
  update: (id, content) => http.patch(`/technical-reports/${id}`, { content }).then(r => r.data),
  refresh: (id) => http.post(`/technical-reports/${id}/refresh-from-os`).then(r => r.data),
  issue: (id) => http.post(`/technical-reports/${id}/issue`).then(r => r.data),
  newVersion: (id) => http.post(`/technical-reports/${id}/new-version`).then(r => r.data),
  share: (id) => http.post(`/technical-reports/${id}/share-links`).then(r => r.data),
  downloadPdf: (id, version) => openBlob(`/technical-reports/${id}/pdf`, version ? { version } : {}, true),
};
export const raaeApi = {
  get: (start, end) => http.get('/raae', { params: { start, end } }).then(r => r.data),
  export: (start, end, fmt) => openBlob('/raae/export', { start, end, fmt }, true),
};
export const laudosApi = {
  list: (q) => http.get('/laudos', { params: q ? { q } : {} }).then(r => r.data),
  create: (data) => http.post('/laudos', data).then(r => r.data),
  update: (id, data) => http.patch(`/laudos/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/laudos/${id}`).then(r => r.data),
};

// ---------- Equipments ----------
export const equipmentsApi = {
  list: (q) => http.get('/equipments', { params: q ? { q } : {} }).then(r => r.data),
  create: (data) => http.post('/equipments', data).then(r => r.data),
  update: (id, data) => http.patch(`/equipments/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/equipments/${id}`).then(r => r.data),
};

// ---------- Gallery ----------
export const galleryApi = {
  list: () => http.get('/gallery').then(r => r.data),
  create: (data) => http.post('/gallery', data).then(r => r.data),
  remove: (id) => http.delete(`/gallery/${id}`).then(r => r.data),
};

// ---------- NFS-e ----------
export const nfseApi = {
  list: () => http.get('/nfse').then(r => r.data),
  create: (data) => http.post('/nfse', data).then(r => r.data),
  issue: (id) => http.post(`/nfse/${id}/issue`).then(r => r.data),
};

// ---------- SST ----------
export const sstApi = {
  list: () => http.get('/sst').then(r => r.data),
  create: (data) => http.post('/sst', data).then(r => r.data),
  remove: (id) => http.delete(`/sst/${id}`).then(r => r.data),
};

// ---------- Fiscal / NFS-e ----------
export const fiscalApi = {
  settings: {
    get: () => http.get('/fiscal/settings').then(r => r.data),
    save: (data) => http.put('/fiscal/settings', data).then(r => r.data),
  },
  cert: {
    upload: (file, password) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', password);
      return http.post('/fiscal/certificate/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    },
    remove: () => http.delete('/fiscal/certificate').then(r => r.data),
  },
  nfse: {
    list: () => http.get('/fiscal/nfse').then(r => r.data),
    get: (id) => http.get(`/fiscal/nfse/${id}`).then(r => r.data),
    fromInvoice: (invoice_id, extras = {}) => http.post('/fiscal/nfse/from-invoice', { invoice_id, ...extras }).then(r => r.data),
    emit: (id) => http.post(`/fiscal/nfse/${id}/emit`).then(r => r.data),
    remove: (id) => http.delete(`/fiscal/nfse/${id}`).then(r => r.data),
    pdfUrl: (id) => `${API}/fiscal/nfse/${id}/pdf`,
  },
};

// ---------- Field / Offline sync ----------
export const fieldApi = {
  sync: (event) => http.post('/field/sync', event).then(r => r.data),
  myOrders: () => http.get('/field/my-orders').then(r => r.data),
};

// ---------- Web Push ----------
export const pushApi = {
  config: () => http.get('/push/config').then(r => r.data),
  subscribe: (data) => http.post('/push/subscribe', data).then(r => r.data),
  unsubscribe: (endpoint) => http.delete('/push/subscribe', { params: { endpoint } }).then(r => r.data),
  subscriptions: () => http.get('/push/subscriptions').then(r => r.data),
  test: () => http.post('/push/test').then(r => r.data),
};

// ---------- Lookup: CNPJ / CEP / CPF ----------
export const lookupApi = {
  cnpj: (doc) => http.get(`/lookup/cnpj/${(doc || '').replace(/\D/g, '')}`).then(r => r.data),
  cep: (cep) => http.get(`/lookup/cep/${(cep || '').replace(/\D/g, '')}`).then(r => r.data),
  cpf: (cpf) => http.get(`/lookup/cpf/${(cpf || '').replace(/\D/g, '')}`).then(r => r.data),
};

// ---------- Finance ----------
export const financeApi = {
  transactions: {
    list: (params) => http.get('/transactions', { params }).then(r => r.data),
    create: (data) => http.post('/transactions', data).then(r => r.data),
    update: (id, data) => http.patch(`/transactions/${id}`, data).then(r => r.data),
    remove: (id) => http.delete(`/transactions/${id}`).then(r => r.data),
    summary: () => http.get('/transactions/summary').then(r => r.data),
  },
  fixedCosts: {
    list: () => http.get('/finance/fixed-costs').then(r => r.data),
    create: (data) => http.post('/finance/fixed-costs', data).then(r => r.data),
    update: (id, data) => http.patch(`/finance/fixed-costs/${id}`, data).then(r => r.data),
    remove: (id) => http.delete(`/finance/fixed-costs/${id}`).then(r => r.data),
    reset: () => http.post('/finance/fixed-costs/reset').then(r => r.data),
  },
  variableCosts: {
    list: () => http.get('/finance/variable-costs').then(r => r.data),
    create: (data) => http.post('/finance/variable-costs', data).then(r => r.data),
    update: (id, data) => http.patch(`/finance/variable-costs/${id}`, data).then(r => r.data),
    remove: (id) => http.delete(`/finance/variable-costs/${id}`).then(r => r.data),
    zeroCategory: (categoria) => http.post('/finance/variable-costs/zero-category', { categoria }).then(r => r.data),
    zeroAll: () => http.post('/finance/variable-costs/zero-all').then(r => r.data),
  },
  variableCostCategories: {
    list: () => http.get('/finance/variable-cost-categories').then(r => r.data),
    create: (data) => http.post('/finance/variable-cost-categories', data).then(r => r.data),
    remove: (id) => http.delete(`/finance/variable-cost-categories/${id}`).then(r => r.data),
  },
  invoices: {
    list: (params) => http.get('/finance/invoices', { params }).then(r => r.data),
    create: (data) => http.post('/finance/invoices', data).then(r => r.data),
    update: (id, data) => http.patch(`/finance/invoices/${id}`, data).then(r => r.data),
    remove: (id) => http.delete(`/finance/invoices/${id}`).then(r => r.data),
  },
  dre: () => http.get('/finance/dre').then(r => r.data),
  transcribe: (blob, filename = 'audio.webm') => {
    const fd = new FormData();
    fd.append('file', blob, filename);
    return http.post('/finance/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  voiceParse: (text) => http.post('/finance/voice-parse', { text }).then(r => r.data),
  voiceConfirm: (data) => http.post('/finance/voice-confirm', data).then(r => r.data),
  voiceLaunch: (text) => http.post('/finance/voice-launch', { text }).then(r => r.data),
  voiceUndo: (target, id) => http.delete(`/finance/voice-launch/${target}/${id}`).then(r => r.data),
};

// ---------- WhatsApp ----------
export const waApi = {
  settings: {
    get: () => http.get('/wa/settings').then(r => r.data),
    save: (data) => http.put('/wa/settings', data).then(r => r.data),
    test: () => http.post('/wa/settings/test').then(r => r.data),
  },
  conversations: {
    list: (params) => http.get('/wa/conversations', { params }).then(r => r.data),
    get: (id) => http.get(`/wa/conversations/${id}`).then(r => r.data),
    update: (id, data) => http.patch(`/wa/conversations/${id}`, data).then(r => r.data),
    takeover: (id) => http.post(`/wa/conversations/${id}/takeover`).then(r => r.data),
    resolve: (id) => http.post(`/wa/conversations/${id}/resolve`).then(r => r.data),
    remove: (id) => http.delete(`/wa/conversations/${id}`).then(r => r.data),
  },
  send: (conversation_id, text) => http.post('/wa/messages/send', { conversation_id, text }).then(r => r.data),
  sendTemplate: (data) => http.post('/wa/messages/template', data).then(r => r.data),
};


export const cesApi = {
  autofill: (os_id) => http.get('/ces/autofill', { params: os_id ? { os_id } : {} }).then(r => r.data),
  list: (params) => http.get('/ces', { params: params || {} }).then(r => r.data),
  get: (id) => http.get(`/ces/${id}`).then(r => r.data),
  create: (data) => http.post('/ces', data).then(r => r.data),
  createFromOs: (os_id, visit_type) => http.post(`/ces/from-os/${os_id}`, null, { params: visit_type ? { visit_type } : {} }).then(r => r.data),
  update: (id, data) => http.patch(`/ces/${id}`, data).then(r => r.data),
  annul: (id, motivo) => http.post(`/ces/${id}/annul`, { motivo }).then(r => r.data),
  stats: () => http.get('/dashboard/ces-stats').then(r => r.data),
  sendLinks: (id, body = {}) => http.post(`/ces/${id}/send-links`, body).then(r => r.data),
  sendWhatsApp: (id, body = {}) => http.post(`/ces/${id}/send-whatsapp`, body).then(r => r.data),
  sendLog: (id) => http.get(`/ces/${id}/send-log`).then(r => r.data),
  addSendLog: (id, body) => http.post(`/ces/${id}/send-log`, body).then(r => r.data),
  pdfBlob: async (id) => {
    const r = await http.get(`/ces/${id}/pdf`, { responseType: 'blob' });
    return { blob: r.data, sha: r.headers['x-pdf-sha256'] || null };
  },
  openPdf: async (id) => {
    let r;
    try { r = await http.get(`/ces/${id}/pdf`, { responseType: 'blob' }); }
    catch (e) {
      let d = null;
      try { d = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      const msg = d?.missing ? `${d.message}\n\nPendências: ${d.missing.join(', ')}` : (d || 'Falha ao gerar o PDF.');
      alert(msg);
      return;
    }
    const url = URL.createObjectURL(r.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
export const rankingApi = {
  get: (params) => http.get('/ranking', { params }).then(r => r.data),
  technician: (id, params) => http.get(`/ranking/technician/${id}`, { params }).then(r => r.data),
  config: () => http.get('/ranking/config').then(r => r.data),
  saveConfig: (data) => http.put('/ranking/config', data).then(r => r.data),
  adjustments: () => http.get('/ranking/adjustments').then(r => r.data),
  createAdjustment: (data) => http.post('/ranking/adjustments', data).then(r => r.data),
  revokeAdjustment: (id, reason) => http.post(`/ranking/adjustments/${id}/revoke`, { reason }).then(r => r.data),
  contests: () => http.get('/ranking/contests').then(r => r.data),
  createContest: (data) => http.post('/ranking/contests', data).then(r => r.data),
  resolveContest: (id, data) => http.post(`/ranking/contests/${id}/resolve`, data).then(r => r.data),
  campaigns: () => http.get('/ranking/campaigns').then(r => r.data),
  campaign: (id) => http.get(`/ranking/campaigns/${id}`).then(r => r.data),
  createCampaign: (data) => http.post('/ranking/campaigns', data).then(r => r.data),
  updateCampaign: (id, data) => http.patch(`/ranking/campaigns/${id}`, data).then(r => r.data),
  deleteCampaign: (id) => http.delete(`/ranking/campaigns/${id}`).then(r => r.data),
  closeCampaign: (id) => http.post(`/ranking/campaigns/${id}/close`).then(r => r.data),
  recalcCampaign: (id, reason) => http.post(`/ranking/campaigns/${id}/recalculate`, { reason }).then(r => r.data),
  awardCampaign: (id, data) => http.post(`/ranking/campaigns/${id}/award`, data).then(r => r.data),
};
rankingApi.dashboard = (period = 'mensal') => http.get('/ranking/dashboard', { params: { period } }).then(r => r.data);
rankingApi.sales = (params) => http.get('/ranking/sales', { params }).then(r => r.data);
